import { useState, useEffect } from 'react';
import { useDevice } from './useDevice';
import { logger } from '../lib/logger';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const { os, browserName } = useDevice();

  // Vérifier si nous sommes dans un contexte PWA
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                      (navigator as any).standalone || 
                      document.referrer.includes('android-app://');

  // Vérifier si nous sommes sur iOS Safari
  const isIOSSafari = os === 'iOS' && browserName === 'Safari';

  // Vérifier si nous sommes sur un navigateur compatible PWA
  const isPWASupported = 'serviceWorker' in navigator && 
                        (isIOSSafari || 'BeforeInstallPromptEvent' in window);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app is already installed
    window.addEventListener('appinstalled', () => {
      setDeferredPrompt(null);
      setIsInstallable(false);
      logger.warn('[PWA] App was installed');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const promptInstall = async () => {
    // Pour iOS Safari
    if (isIOSSafari && !isStandalone) {
      return true; // On considère que l'utilisateur va suivre les instructions
    }

    // Pour les autres navigateurs
    if (!deferredPrompt) {
      return false;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      // Clear the saved prompt since it can't be used again
      setDeferredPrompt(null);
      setIsInstallable(false);

      return outcome === 'accepted';
    } catch (error) {
      console.error('[PWA] Error showing install prompt:', error);
      return false;
    }
  };

  return {
    isInstallable: isPWASupported && !isStandalone && (isIOSSafari || deferredPrompt !== null),
    isIOSSafari,
    promptInstall
  };
};